import React, { useMemo } from 'react';
import { Box, Typography, Button, FormControl, Select, MenuItem, InputLabel, Checkbox, OutlinedInput, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Define theme constants for reusability
const theme = {
  primaryColor: '#F9E719',
  hoverColor: '#E9D800',
  borderColor: '#95A0B3',
  shadow: '0px 2px 5px 0px #00000040',
};

export default function VehiclesSection({
  vehicles = [],
  selectedVehicles = [],
  handleVehicleSelection,
  createVehicle,
  isMobile,
}) {
  const { t } = useTranslation();
  // Memoize vehicle lookup map for O(1) access
  const vehicleMap = useMemo(() => {
    return vehicles.reduce((map, vehicle) => {
      map[vehicle.id] = vehicle.name;
      return map;
    }, {});
  }, [vehicles]);

  // Handle checkbox toggle and directly update selectedVehicles
  const handleCheckboxChange = (vehicleId) => {
    const newSelectedVehicles = selectedVehicles.includes(vehicleId)
      ? selectedVehicles.filter((id) => id !== vehicleId)
      : [...selectedVehicles, vehicleId];
    handleVehicleSelection({ target: { value: newSelectedVehicles } });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width:"auto",
        borderRadius: '10px',
        border: '1px solid #A5DCF3',
        padding: '12px',
        gap: '16px',
        boxShadow: '-2px 2px 5px 0px #00000040',
        display: 'flex',
      }}
    >
      <Box
        sx={{
          bgcolor: 'white',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: '#E8EAEE',
            minHeight: '60px',
            borderRadius: "4px",
            padding: { xs: '10px', md: '14px 10px' },
            boxShadow: " 0px 2px 5px 0px #00000040",
            borderBottom: '1px solid #E0E0E0',
            display: 'flex',
            flexDirection: { xs: isMobile ? 'column' : 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: isMobile ? 'flex-start' : 'center' },
            gap: '16px',
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: "16px", color: "#183059" }}>
            {t("Vehicles on this Site")}
          </Typography>
        </Box>

        {/* Vehicle Selection with Checkboxes */}
        <Box
          sx={{ py: { xs: 2, md: 3 } }}
        >
          <FormControl
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                border: `2px solid ${theme.borderColor}`,
                "& fieldset": {
                  border: "none",
                },
              },
            }}
          >
            {/* <InputLabel sx={{ mt: "4px", bgcolor: "#FFF" }}>Select Vehicles from the List</InputLabel> */}
            <Select
              displayEmpty
              multiple
              value={selectedVehicles}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                const selectedNames = selected
                  .map((id) => vehicleMap[id])
                  .filter((name) => typeof name === 'string' && name.trim().length > 0);

                if (selectedNames.length === 0) {
                  return <Typography component="span" color="text.secondary">All Vehicles</Typography>;
                }

                return selectedNames.join(', ');
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 170,
                    width: 250,
                  },
                },
              }}
              sx={{ height: vehicles.length > 0 ? 'auto' : '150px' }}
            >
              {vehicles.length > 0 ? (
                vehicles.map((vehicle) => (
                  <MenuItem key={vehicle.id} value={vehicle.id} disableGutters>
                    <Checkbox
                      checked={selectedVehicles.includes(vehicle.id)}
                      onChange={() => handleCheckboxChange(vehicle.id)}
                      sx={{
                        color: theme.borderColor,
                        '&.Mui-checked': { color: '#20A8E0' },
                        mr: 1,
                      }}
                    />
                    {vehicle.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <Typography color="text.secondary">No vehicles available</Typography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{
          display: 'flex',
          borderRadius: '8px',
          padding: "12px",
          justifyContent: "space-between",
          border: '1px solid #B7BFCC',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 1,
        }}>
          <Typography
            variant="body2"
            sx={{
              color: '#183059',
              fontSize: '14px',
              fontWeight: 500,
              whiteSpace: 'nowrap'
            }}
          >
            {t('No vehicles to select?')}
          </Typography>
          <Button
            variant="contained"
            onClick={createVehicle}
            sx={{
              fontWeight: 600,
              height: '55px',
              textTransform: "none",
              borderRadius: '8px',
              padding: '10px 20px',
              bgcolor: theme.primaryColor,
              boxShadow: "none",
              border: '1px solid #414958',
              color: '#1F242E',
              '&:hover': { bgcolor: theme.hoverColor },
              mt: { xs: isMobile ? 1 : 0 },
            }}
          >
            + {t("Create Vehicles")}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}